import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, RotateCcw, Lock, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABELS = { capture: "Call Capture", poc: "POC Details", head_office: "Head Office" };
const TYPES = ["text", "textarea", "select", "date", "datetime", "number", "phone", "email"];
const EMPTY = { label: "", section: "capture", type: "text", required: false, dropdown_key: "" };

/** §8.3 Dynamic Field Management — configure the lead form without code. */
export default function LeadFieldsManager() {
  const { activeSlug } = useClient();
  const [fields, setFields] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/lead-fields", { params: { include_deleted: true } })
      .then((r) => setFields(r.data.fields || []))
      .catch(() => toast.error("Failed to load field configuration"));
  }, []);
  useEffect(() => { load(); }, [load, activeSlug]);

  const patch = async (f, update) => {
    try {
      const { data } = await api.patch(`/lead-fields/${f.id}`, update);
      setFields((rows) => rows.map((r) => (r.id === f.id ? data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const softDelete = async (f) => {
    try {
      await api.delete(`/lead-fields/${f.id}`);
      toast.success(`"${f.label}" deleted (soft) — restorable below`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const restore = async (f) => {
    try {
      await api.post(`/lead-fields/${f.id}/restore`);
      toast.success(`"${f.label}" restored`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const createField = async () => {
    if (!form.label.trim()) { toast.error("Field label is required"); return; }
    if (form.type === "select" && !form.dropdown_key.trim()) {
      toast.error("Select fields need a Dropdown Catalogue key"); return;
    }
    setSaving(true);
    try {
      await api.post("/lead-fields", {
        ...form,
        dropdown_key: form.type === "select" ? form.dropdown_key.trim() : null,
      });
      toast.success("Field added to the lead form");
      setDialogOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const active = fields.filter((f) => !f.deleted_at);
  const deleted = fields.filter((f) => f.deleted_at);

  return (
    <Card data-testid="lead-fields-manager">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-slate-500" /> Dynamic Field Management
        </CardTitle>
        <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-lead-field-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Field
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-[11px] text-slate-500">
          Configure the agent lead form without development — rename fields, toggle Mandatory / Visible /
          Read-only, and add custom fields. Built-in fields can be configured but not deleted;
          <Lock className="w-3 h-3 inline mx-1" />locked fields drive core workflow logic.
        </p>
        {["capture", "poc", "head_office"].map((section) => (
          <div key={section}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{SECTION_LABELS[section]}</div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left px-3 py-1.5">Label</th>
                    <th className="text-left px-2 py-1.5">Type</th>
                    <th className="text-left px-2 py-1.5">Options Source</th>
                    <th className="text-center px-2 py-1.5">Mandatory</th>
                    <th className="text-center px-2 py-1.5">Visible</th>
                    <th className="text-center px-2 py-1.5">Read-only</th>
                    <th className="text-center px-2 py-1.5">Order</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {active.filter((f) => f.section === section).map((f) => (
                    <tr key={f.id} className="border-t border-slate-100" data-testid={`field-row-${f.key}`}>
                      <td className="px-3 py-1.5">
                        <InlineLabel field={f} onSave={(label) => patch(f, { label })} />
                      </td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className="text-[9px]">{f.type}</Badge>
                        {f.locked && <Lock className="w-3 h-3 inline ml-1 text-slate-400" />}
                        {!f.system && <Badge className="ml-1 text-[9px] bg-indigo-100 text-indigo-700 hover:bg-indigo-100">custom</Badge>}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">{f.dropdown_key || "—"}</td>
                      <td className="px-2 py-1.5 text-center">
                        <Switch checked={!!f.required} disabled={f.locked}
                                onCheckedChange={(v) => patch(f, { required: v })}
                                data-testid={`field-required-${f.key}`} className="scale-75" />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Switch checked={!!f.visible} disabled={f.locked}
                                onCheckedChange={(v) => patch(f, { visible: v })}
                                data-testid={`field-visible-${f.key}`} className="scale-75" />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Switch checked={!!f.read_only}
                                onCheckedChange={(v) => patch(f, { read_only: v })}
                                data-testid={`field-readonly-${f.key}`} className="scale-75" />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Input type="number" defaultValue={f.sort_order} className="h-6 w-14 text-[10px] mx-auto"
                               onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== f.sort_order) patch(f, { sort_order: v }); }} />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {!f.system && (
                          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-rose-600"
                                  onClick={() => softDelete(f)} data-testid={`field-delete-${f.key}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {deleted.length > 0 && (
          <div>
            <button className="text-[11px] text-slate-500 underline" onClick={() => setShowDeleted((s) => !s)}
                    data-testid="toggle-deleted-fields">
              {showDeleted ? "Hide" : "Show"} deleted fields ({deleted.length})
            </button>
            {showDeleted && (
              <div className="mt-2 space-y-1">
                {deleted.map((f) => (
                  <div key={f.id} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-3 py-1.5">
                    <span className="line-through text-slate-500">{f.label} <span className="font-mono text-[10px]">({f.key})</span></span>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-700"
                            onClick={() => restore(f)} data-testid={`field-restore-${f.key}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">Add lead form field</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Label</Label>
              <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                     className="h-8 mt-1 text-xs" data-testid="new-field-label" placeholder="e.g. Preferred Contact Time" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Section</Label>
                <Select value={form.section} onValueChange={(v) => setForm((f) => ({ ...f, section: v }))}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="new-field-section"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SECTION_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="new-field-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.type === "select" && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">Dropdown Catalogue key</Label>
                <Input value={form.dropdown_key} onChange={(e) => setForm((f) => ({ ...f, dropdown_key: e.target.value }))}
                       className="h-8 mt-1 text-xs font-mono" data-testid="new-field-dropdown-key"
                       placeholder="e.g. contact_time (manage values in Dropdown Catalogue)" />
              </div>
            )}
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={form.required} onCheckedChange={(v) => setForm((f) => ({ ...f, required: v }))}
                      className="scale-75" data-testid="new-field-required" />
              Mandatory (enforced when disposition = Data Captured)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={createField} disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-field-save">
              {saving ? "Adding…" : "Add Field"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function InlineLabel({ field, onSave }) {
  const [val, setVal] = useState(field.label);
  useEffect(() => setVal(field.label), [field.label]);
  return (
    <Input value={val} onChange={(e) => setVal(e.target.value)}
           onBlur={() => { if (val.trim() && val !== field.label) onSave(val.trim()); }}
           className="h-7 text-xs border-transparent hover:border-slate-200 focus:border-indigo-300 px-1.5"
           data-testid={`field-label-${field.key}`} />
  );
}
